import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  faChevronLeft,
  faChevronRight,
  faClock,
  faDownload,
  faMagicWandSparkles,
  faTimes,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useLanguage } from "../../contexts/LanguageContext";
import { useModalScrollLock } from "../../hooks/useModalScrollLock";

import { enhancementOptionsList } from "./constants";

import "./PhotoSlider.css";

const PhotoSlider = ({
  isPremium = false,
  photos,
  initialIndex = 0,
  isOpen,
  onClose,
  onDelete,
  onDownload,
  onEnhanceWithAI,
  onEnhanceStart, // optional callback to show loading toast externally
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [enhancementType, setEnhancementType] = useState("professional");
  const { t } = useLanguage();

  const enhancementOptions = enhancementOptionsList(t);

  const [showPromptModal, setShowPromptModal] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [pendingEnhance, setPendingEnhance] = useState(false);
  const promptModalRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !showPromptModal) return;
    const trigger = document.activeElement;
    promptModalRef.current?.querySelector("textarea")?.focus();
    return () => trigger?.focus();
  }, [isOpen, showPromptModal]);

  // Lock scroll when main slider open or custom prompt modal open
  useModalScrollLock(isOpen || showPromptModal);
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (showPromptModal) {
        if (e.key === "Escape" && !pendingEnhance) {
          e.preventDefault();
          setShowPromptModal(false);
        }
        if (e.key === "Tab") {
          const controls = promptModalRef.current?.querySelectorAll(
            "textarea, button:not(:disabled)"
          );
          if (controls?.length) {
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (e.shiftKey && document.activeElement === first) {
              e.preventDefault();
              last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
        }
        return;
      }

      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowLeft":
          setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
          break;
        case "ArrowRight":
          setCurrentIndex((prev) => (prev + 1) % photos.length);
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, showPromptModal, pendingEnhance, onClose, photos.length]);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  const currentPhoto = photos[currentIndex];

  if (!isOpen || !currentPhoto) return null;

  return createPortal(
    <>
      <div className="photo-slider-overlay" onClick={onClose} inert={showPromptModal}>
        <div
          className="photo-slider-container"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="photo-slider-header">
            <div className="photo-info">
              <span className="photo-counter">
                {currentIndex + 1} of {photos.length}
              </span>
            </div>
            <div className="photo-actions-slider">
              {isPremium && (
                <>
                  {onEnhanceWithAI && (
                    <div className="enhance-group enhance-group-desktop">
                      <select
                        id="select-photo-style"
                        className="enhance-style-select"
                        value={enhancementType}
                        onChange={(e) => {
                          const v = e.target.value;
                          setEnhancementType(v);
                          if (v === "custom") {
                            setShowPromptModal(true);
                          }
                        }}
                        title="Enhancement style"
                      >
                        {enhancementOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <button
                        className="enhance-btn"
                        onClick={() => {
                          if (enhancementType === "custom") {
                            setShowPromptModal(true);
                            return;
                          }
                          if (onEnhanceStart)
                            onEnhanceStart(currentPhoto, enhancementType);
                          onEnhanceWithAI(currentPhoto, enhancementType);
                        }}
                        title={`Enhance (${enhancementType})`}
                      >
                        <FontAwesomeIcon icon={faMagicWandSparkles} />
                        {t("commons.apply")}
                      </button>
                    </div>
                  )}
                  {onDownload && (
                    <button
                      className="action-btn"
                      onClick={() => onDownload(currentPhoto)}
                      title="Download"
                    >
                      <FontAwesomeIcon icon={faDownload} />
                    </button>
                  )}
                  {onDelete && (
                    <button
                      className="action-btn delete-btn"
                      onClick={() => onDelete(currentPhoto.id)}
                      title="Delete"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </>
              )}
              <button
                className="action-btn close-btn"
                onClick={onClose}
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          {isPremium && (
            <>
              {onEnhanceWithAI && (
                <div className="enhance-group enhance-group-mobile">
                  <select
                    id="select-photo-style"
                    className="enhance-style-select"
                    value={enhancementType}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEnhancementType(v);
                      if (v === "custom") {
                        setShowPromptModal(true);
                      }
                    }}
                    title="Enhancement style"
                  >
                    {enhancementOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <button
                    className="enhance-btn"
                    onClick={() => {
                      if (enhancementType === "custom") {
                        setShowPromptModal(true);
                        return;
                      }
                      if (onEnhanceStart)
                        onEnhanceStart(currentPhoto, enhancementType);
                      onEnhanceWithAI(currentPhoto, enhancementType);
                    }}
                    title={`Enhance (${enhancementType})`}
                  >
                    <FontAwesomeIcon
                      icon={faMagicWandSparkles}
                      style={{ marginRight: "4px" }}
                    />
                    {t("commons.apply")}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Main Image */}
          <div className="photo-slider-content">
            <img
              src={currentPhoto.url}
              alt={`Photo ${currentPhoto.id}`}
              className="slider-image"
            />

            {/* Navigation Arrows */}
            {photos.length > 1 && (
              <>
                <button
                  className="nav-arrow nav-arrow-left"
                  onClick={goToPrevious}
                  title="Previous photo"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button
                  className="nav-arrow nav-arrow-right"
                  onClick={goToNext}
                  title="Next photo"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {photos.length > 1 && (
            <div className="photo-slider-thumbnails">
              {photos.map((photo, index) => (
                <div
                  key={photo.id}
                  className={`thumbnail ${
                    index === currentIndex ? "active" : ""
                  }`}
                  onClick={() => setCurrentIndex(index)}
                >
                  <img src={photo.thumbnail} alt={`Thumbnail ${photo.id}`} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showPromptModal && (
        <div
          className="prompt-modal-overlay"
          onClick={() => {
            if (!pendingEnhance) setShowPromptModal(false);
          }}
        >
          <div
            ref={promptModalRef}
            className="prompt-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="custom-prompt-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="prompt-modal-header">
              <span className="prompt-eyebrow">
                <FontAwesomeIcon icon={faMagicWandSparkles} />
                {t("photoEnhancement.promptStudio")}
              </span>
              <button
                className="prompt-close"
                aria-label={t("commons.cancel")}
                onClick={() => setShowPromptModal(false)}
                disabled={pendingEnhance}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="prompt-modal-body">
              <h3 id="custom-prompt-title">{t("photoEnhancement.promptTitle")}</h3>
              <p className="prompt-intro">{t("photoEnhancement.promptDescription")}</p>
              <div className="prompt-photo-context">
                <img src={currentPhoto.url} alt="" />
                <div>
                  <span>{t("photoEnhancement.selectedPhoto")}</span>
                  <strong>{t("photoEnhancement.promptPhoto", { current: currentIndex + 1, total: photos.length })}</strong>
                </div>
              </div>
              <label className="prompt-field-label" htmlFor="custom-photo-prompt">
                {t("photoEnhancement.promptLabel")}
              </label>
              <textarea
                id="custom-photo-prompt"
                aria-describedby="custom-prompt-hint"
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder={t("photoEnhancement.promptExample")}
                rows={4}
              />
              <p id="custom-prompt-hint" className="prompt-hint">{t("photoEnhancement.promptHint")}</p>
              <div className="prompt-suggestions" role="group" aria-label={t("photoEnhancement.promptIdeas")}>
                <span>{t("photoEnhancement.promptIdeas")}</span>
                <div>
                  {["Light", "Film", "Portrait"].map((idea) => (
                    <button
                      key={idea}
                      type="button"
                      onClick={() => {
                        setCustomPrompt(t(`photoEnhancement.prompt${idea}Text`));
                        promptModalRef.current?.querySelector("textarea")?.focus();
                      }}
                    >
                      {t(`photoEnhancement.prompt${idea}`)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="prompt-modal-actions">
              <p className="prompt-time-notice">
                <FontAwesomeIcon icon={faClock} />
                {t("photoEnhancement.promptTimeNotice")}
              </p>
              <button
                className="action-btn close-btn"
                onClick={() => {
                  setShowPromptModal(false);
                  setPendingEnhance(false);
                }}
                disabled={pendingEnhance}
              >
                {t("commons.cancel")}
              </button>
              <button
                className="action-btn enhance-btn"
                disabled={!customPrompt.trim()}
                onClick={() => {
                  if (onEnhanceStart)
                    onEnhanceStart(currentPhoto, "custom", customPrompt.trim());
                  onEnhanceWithAI(currentPhoto, "custom", customPrompt.trim());
                  setPendingEnhance(false);
                  setShowPromptModal(false);
                  setCustomPrompt("");
                }}
              >
                <FontAwesomeIcon icon={faMagicWandSparkles} />
                {t("photoEnhancement.promptApply")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default PhotoSlider;
